import React from 'react'
import { useSelector } from 'react-redux'
import { volumeSnapshotsByIdSelector } from 'app/plugins/openstack/components/storage/snapshots/selectors'
import SimpleLink from 'core/components/SimpleLink'
import Text from 'core/elements/Text'
import { routeManager } from 'core/utils/routes'
import { ImageDetailsPageTabs } from 'openstack/components/images/details/model'
import { imagesByIdSelector } from 'openstack/components/images/selectors'
import { IVolumeSnapshotDetailsPageTabs } from 'openstack/components/storage/snapshots/details/model'
import { Snapshot, Volume } from '../models'
import { volumesByIdSelector } from '../selectors'
import { getBackingImageId } from '../utils/volumeUtils'
import { IVolumeDetailsPageTabs } from './model'

type ResourceLinkWithTooltipProps = {
  label: string
  src: string
  children: React.ReactNode
}

export const ResourceLinkWithTooltip: React.FC<ResourceLinkWithTooltipProps> = ({
  label,
  src,
  children,
}) => (
  <Text variant="caption1">
    {label} <SimpleLink src={src}>{children}</SimpleLink>
  </Text>
)

type VolumeSourceCellProps = {
  item: Partial<Volume>
}

const renderImageLink = (imageId: string, imageName: string) => {
  return (
    <ResourceLinkWithTooltip
      label="Image:"
      src={routeManager.routes.openstack.imageDetails.path({
        id: imageId,
        detailsTab: ImageDetailsPageTabs.Overview,
      })}
    >
      {imageName}
    </ResourceLinkWithTooltip>
  )
}

const renderVolumeSourceLink = (sourceVolume: string, volumesById: Record<string, Volume>) => {
  const volume = volumesById[sourceVolume]
  const volumeNameOrId = volume?.name || volume?.id || sourceVolume
  return (
    <ResourceLinkWithTooltip
      label="Volume:"
      src={routeManager.routes.openstack.volumeDetails.path({
        id: volume?.id || sourceVolume,
        tab: IVolumeDetailsPageTabs.Overview,
      })}
    >
      {volumeNameOrId}
    </ResourceLinkWithTooltip>
  )
}

const renderSnapshotSourceLink = (
  snapshotId: string,
  volumeSnapshotsById: Record<string, Snapshot>,
) => {
  const snapshot = volumeSnapshotsById[snapshotId]
  const snapshotNameOrId = snapshot?.name || snapshotId
  return (
    <ResourceLinkWithTooltip
      label="Volume Snapshot:"
      src={routeManager.routes.openstack.volumeSnapshotDetails.path({
        id: snapshotId,
        tab: IVolumeSnapshotDetailsPageTabs.Overview,
      })}
    >
      {snapshotNameOrId}
    </ResourceLinkWithTooltip>
  )
}

// VSRC-FR-001, VSRC-FR-003: created-from-image (volume_image_metadata) — the common case
// VSRC-FR-002, VSRC-FR-004: Glance-store backing-image (getBackingImageId) — distinct special case
export const VolumeSourceCell: React.FC<VolumeSourceCellProps> = ({ item }) => {
  const volumesById = useSelector(volumesByIdSelector)
  const volumeSnapshotsById = useSelector(volumeSnapshotsByIdSelector)
  const imagesById = useSelector(imagesByIdSelector)
  const sourceVolume = item?.source_volid

  if (sourceVolume) {
    return renderVolumeSourceLink(sourceVolume, volumesById)
  }

  if (item?.snapshot_id) {
    return renderSnapshotSourceLink(item.snapshot_id, volumeSnapshotsById)
  }

  const createdFromImageId = item?.volume_image_metadata?.image_id
  if (createdFromImageId) {
    const imageName =
      imagesById[createdFromImageId]?.name ||
      item?.volume_image_metadata?.image_name ||
      createdFromImageId
    return renderImageLink(createdFromImageId, imageName)
  }

  const backingImageId = getBackingImageId(item)
  if (backingImageId) {
    const imageName = imagesById[backingImageId]?.name || backingImageId
    return renderImageLink(backingImageId, imageName)
  }

  return null
}
